import { useState } from "react";
import {
  useApplyGtPoppedia,
  useGtPoppediaSearch,
  useGtPoppediaSuggestion,
} from "../hooks/useInventory.js";
import type { GtPoppediaPopSummary, Item } from "../types.js";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Field,
  Input,
  Modal,
  Spinner,
} from "./ui.js";

function currency(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `$${value.toFixed(2)}`;
}

function PopResult({ pop, selected, onClick }: { pop: GtPoppediaPopSummary; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition ${
        selected
          ? "border-indigo-500 bg-indigo-950/30"
          : "border-slate-800 bg-slate-900/40 hover:border-slate-700"
      }`}
    >
      {pop.image_path ? (
        <img
          src={pop.image_path}
          alt={pop.pop_name}
          className="h-16 w-16 flex-shrink-0 rounded-md object-contain"
          loading="lazy"
        />
      ) : (
        <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-md bg-slate-800 text-xs text-slate-400">
          No image
        </div>
      )}
      <div className="min-w-0 flex-1 space-y-1">
        <p className="truncate text-sm font-medium text-slate-100">{pop.pop_name}</p>
        <div className="flex flex-wrap gap-1">
          {pop.pop_box_number ? <Badge tone="neutral">Box {pop.pop_box_number}</Badge> : null}
          {pop.series_name ? <Badge tone="neutral">{pop.series_name}</Badge> : null}
          {pop.exclusivity_name ? <Badge tone="amber">{pop.exclusivity_name}</Badge> : null}
          {pop.rarity_name ? <Badge tone="indigo">{pop.rarity_name}</Badge> : null}
        </div>
        <p className="text-xs text-slate-400">
          Avg {currency(pop.avg_price)} · Min {currency(pop.min_price)} · Max {currency(pop.max_price)}
          {pop.sale_count !== null ? ` · ${pop.sale_count} sales` : null}
        </p>
      </div>
    </button>
  );
}

interface GtPoppediaPickerProps {
  item: Item;
  onClose: () => void;
  onApplied: () => void;
}

export function GtPoppediaPicker({ item, onClose, onApplied }: GtPoppediaPickerProps) {
  const [query, setQuery] = useState(item.name);
  const [debouncedQuery, setDebouncedQuery] = useState(item.name);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);

  const search = useGtPoppediaSearch(debouncedQuery, true);
  const suggestion = useGtPoppediaSuggestion(selectedUuid);
  const apply = useApplyGtPoppedia();

  function handleSearch() {
    setDebouncedQuery(query);
  }

  function handleApply() {
    if (!selectedUuid) return;
    apply.mutate(
      { itemId: item.id, popUuid: selectedUuid },
      {
        onSuccess: () => {
          onApplied();
          onClose();
        },
      },
    );
  }

  const isLoading = search.isLoading || (selectedUuid && suggestion.isLoading);
  const error = search.error ?? suggestion.error ?? apply.error;

  return (
    <Modal title="Funko Metadata" onClose={onClose} wide>
      <div className="space-y-4">
        {error ? <ErrorBanner message={(error as Error).message ?? "Request failed"} /> : null}

        <div className="flex gap-2">
          <Field label="Search Funko Metadata" className="flex-1">
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") handleSearch();
              }}
              placeholder="Name, box number, series, exclusivity…"
            />
          </Field>
          <div className="pt-5">
            <Button onClick={handleSearch} disabled={search.isLoading}>
              Search
            </Button>
          </div>
        </div>

        {isLoading ? <Spinner /> : null}

        {!isLoading && search.data && search.data.results.length === 0 ? (
          <EmptyState title="No matches found" hint="Try a broader name or leave out the box number." />
        ) : null}

        {!isLoading && search.data && search.data.results.length > 0 ? (
          <div className="grid max-h-96 grid-cols-1 gap-2 overflow-y-auto pr-1">
            {search.data.results.map((pop) => (
              <PopResult
                key={pop.pop_uuid}
                pop={pop}
                selected={selectedUuid === pop.pop_uuid}
                onClick={() => setSelectedUuid(pop.pop_uuid)}
              />
            ))}
          </div>
        ) : null}

        {suggestion.data ? (
          <Card>
            <h3 className="mb-2 text-sm font-semibold text-slate-200">Suggested merge</h3>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-xs sm:grid-cols-2">
              <div className="flex justify-between gap-2 sm:block">
                <dt className="text-slate-400">Name</dt>
                <dd className="text-slate-200">{suggestion.data.name ?? "—"}</dd>
              </div>
              <div className="flex justify-between gap-2 sm:block">
                <dt className="text-slate-400">License</dt>
                <dd className="text-slate-200">{suggestion.data.license ?? "—"}</dd>
              </div>
              <div className="flex justify-between gap-2 sm:block">
                <dt className="text-slate-400">Box #</dt>
                <dd className="text-slate-200">{suggestion.data.boxNo ?? "—"}</dd>
              </div>
              <div className="flex justify-between gap-2 sm:block">
                <dt className="text-slate-400">Series</dt>
                <dd className="text-slate-200">{suggestion.data.series ?? "—"}</dd>
              </div>
              <div className="flex justify-between gap-2 sm:block">
                <dt className="text-slate-400">Exclusivity</dt>
                <dd className="text-slate-200">{suggestion.data.exclusiveTo ?? "—"}</dd>
              </div>
              <div className="flex justify-between gap-2 sm:block">
                <dt className="text-slate-400">Current value</dt>
                <dd className="text-slate-200">{currency(suggestion.data.valuation.avgPrice)}</dd>
              </div>
              <div className="flex justify-between gap-2 sm:block">
                <dt className="text-slate-400">For-sale price</dt>
                <dd className="text-slate-200">{currency(suggestion.data.valuation.maxPrice)}</dd>
              </div>
              <div className="flex justify-between gap-2 sm:block">
                <dt className="text-slate-400">Valuation date</dt>
                <dd className="text-slate-200">{suggestion.data.valuation.valuationDate ?? "—"}</dd>
              </div>
            </dl>
            <p className="mt-2 text-xs text-slate-400">
              Your existing data is kept. Funko Metadata fills empty fields and updates valuation only.
            </p>
          </Card>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!selectedUuid || apply.isPending}>
            {apply.isPending ? "Applying…" : "Apply to item"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
